/**
 * Deterministic SHA256 hashing for asset content.
 *
 * One algorithm shared by:
 *   - glob discovery (`discover.ts`)
 *   - author-shipped manifest discovery (`discover-manifest.ts`)
 *   - virtual-tree discovery (`tree-entries.ts`, via its own copy using async fetch)
 *
 * Hashing rule: files sorted alphabetically. For each file feed
 *   `path` + `\0` + `content` + `\0`
 * into SHA256. Yields the same hash regardless of OS or filesystem ordering.
 *
 * Files that are referenced by the file filter but absent on disk are skipped
 * (non-fatal): they typically represent optional companion files like
 * `validator.py` that may not exist for every asset. The author-manifest path
 * surfaces missing files as a hard error before reaching the hash step, so
 * this only matters for the glob discovery code path.
 */

import * as crypto from "node:crypto";
import * as fs from "node:fs";
import * as path from "node:path";

/**
 * Compute a deterministic SHA256 hash over a set of files.
 *
 * @param assetRoot - Absolute path to the asset root directory
 * @param files     - Relative file paths (from `assetRoot`) to include in the hash
 * @returns Hex-encoded SHA256 digest
 * @docLink packages/discovery/concepts#compute-deterministic-hash
 */
export function computeDeterministicHash(assetRoot: string, files: string[]): string {
  const hash = crypto.createHash("sha256");
  const sorted = [...files].sort();
  for (const file of sorted) {
    const fullPath = path.join(assetRoot, file);
    try {
      hash.update(file);
      hash.update("\0");
      const content = fs.readFileSync(fullPath);
      hash.update(content);
      hash.update("\0");
    } catch {
      // File doesn't exist — skip (non-fatal, filter may reference optional
      // files like validator.py).
    }
  }
  return hash.digest("hex");
}

/**
 * Compute SHA256 over a curated file set with full visibility into which
 * files contributed bytes vs were skipped because they're missing on disk.
 *
 * Used by the author-shipped manifest verifier where missing files are a
 * hard error, not a silent skip.
 *
 * @param assetRoot - Absolute path to the asset root directory
 * @param files     - Relative file paths (from `assetRoot`) to include
 * @returns `{ sha256, present, missing }` — present/missing are relative paths
 * @docLink packages/discovery/concepts#compute-deterministic-hash-detailed
 */
export function computeDeterministicHashDetailed(
  assetRoot: string,
  files: string[],
): { sha256: string; present: string[]; missing: string[] } {
  const hash = crypto.createHash("sha256");
  const sorted = [...files].sort();
  const present: string[] = [];
  const missing: string[] = [];
  for (const file of sorted) {
    const fullPath = path.join(assetRoot, file);
    let content: Buffer | null = null;
    try {
      content = fs.readFileSync(fullPath);
    } catch {
      missing.push(file);
      continue;
    }
    hash.update(file);
    hash.update("\0");
    hash.update(content);
    hash.update("\0");
    present.push(file);
  }
  return { sha256: hash.digest("hex"), present, missing };
}
